import { ButtonLink, FlyIn, IconButton, OverlayLoader } from '@coop/components';
import { UserIcon } from '@coop/icons';
import React from 'react';

import { useIsFromXXXSmall } from '../../../common/components/atoms/ResponsiveBreakpoints';
import useModal from '../../../common/hooks/useModal';
import { useIsUserAuthenticated, useIsUserCompany } from '../../../common/hooks/useUserProfile';
import { FlyInType } from '../../../common/store/structureDefinitions/modalState';
import { ga4UserTracking } from '../../../common/tracking/ga4/user';
import { isInB2BCrossContext, useIsB2BRoute } from '../../../common/utils/b2bUtils';
import { getLoginUrl, getLoginUrlWithReturnUrl } from '../../../common/utils/urlUtils';
import { useIsDarkMode } from '../../../ecommerce/hooks/useIsDarkMode';
import MyCoopProfileMenu from '../MyCoopProfileFlyIn/MyCoopProfileMenu';
import CncTrigger from './CncTrigger/CncTrigger';
import { HeaderMyFavoritesDropdown, HeaderProfileDotDropdown } from './HeaderProfileDropdown';
import styles from './HeaderRightNav.module.scss';

const ProfileNav = (props: { disableMyFavoritesMenu: boolean }) => {
    const { open, isOpen, close } = useModal(FlyInType.MyProfileMenu);
    const isDarkMode = useIsDarkMode();
    const buttonTheme = isDarkMode ? 'outlined-inverse' : 'outlined-subtle';

    return (
        <>
            <div className="HeaderBanner-ProfileNav">
                {!props.disableMyFavoritesMenu && <HeaderMyFavoritesDropdown />}
                <IconButton
                    icon={<UserIcon />}
                    label="Mitt coop"
                    onClick={open}
                    theme={buttonTheme}
                />
            </div>
            <div className="HeaderBanner-ProfileDotNav">
                {!props.disableMyFavoritesMenu ? (
                    <HeaderProfileDotDropdown />
                ) : (
                    <IconButton
                        icon={<UserIcon />}
                        label="Mitt coop"
                        onClick={open}
                        theme={buttonTheme}
                    />
                )}
            </div>
            <FlyIn.Root
                isOpen={isOpen}
                close={close}
                sizePx={375}
                contentClassName={styles.FlyInBonus2026}
            >
                <React.Suspense fallback={<OverlayLoader isLoading />}>
                    <MyCoopProfileMenu navigationSlot={<FlyIn.Navigation onClose={close} />} />
                </React.Suspense>
            </FlyIn.Root>
        </>
    );
};

const HeaderRightNav = (props: {
    disableMyFavoritesMenu: boolean;
    stayAfterLoginLogoutRedirect: boolean;
    headerTheme: 'black' | 'darkGreen';
}) => {
    const isAuthenticated = useIsUserAuthenticated();
    const isB2BRoute = useIsB2BRoute();
    const isFromXXXSmall = useIsFromXXXSmall();

    const { companyUserOnPrivateSite, privateUserOnCompanySite } = isInB2BCrossContext({
        isB2BRoute,
        isUserCompany: useIsUserCompany(),
        isAuthenticated,
    });

    const onLoginClickTracking = () => {
        ga4UserTracking.login(null, 'start', 'header');
    };

    if (privateUserOnCompanySite) {
        return (
            <ButtonLink
                theme="primary"
                className="LoginButton"
                href={getLoginUrl('company', 'login')}
                rel="nofollow"
                onClick={onLoginClickTracking}
            >
                Logga in företag
            </ButtonLink>
        );
    }

    if (companyUserOnPrivateSite) {
        return (
            <ButtonLink
                theme="primary"
                className="LoginButton"
                href={getLoginUrl('private', 'login')}
                rel="nofollow"
                onClick={onLoginClickTracking}
            >
                Logga in privat
            </ButtonLink>
        );
    }

    return (
        <>
            {!isAuthenticated ? (
                <ButtonLink
                    theme="primary"
                    className="LoginButton"
                    rel="nofollow"
                    href={getLoginUrlWithReturnUrl(
                        isB2BRoute ? 'company' : 'private',
                        'none',
                        props.stayAfterLoginLogoutRedirect,
                    )}
                    onClick={onLoginClickTracking}
                >
                    Logga in
                </ButtonLink>
            ) : (
                <ProfileNav disableMyFavoritesMenu={props.disableMyFavoritesMenu} />
            )}
            <CncTrigger
                withTotalPrice={isFromXXXSmall}
                badgeColor={props.headerTheme === 'darkGreen' ? 'white' : 'ctaGreen'}
            />
        </>
    );
};

export default HeaderRightNav;
